import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { MatTableModule } from '@angular/material/table';
import { TranslateModule } from '@ngx-translate/core';
import { ApiService } from '../../_services/api.service';
import { Subject } from '../../_interfaces/subject';
import { FormattedVisit, Visit, formatVisit } from '../../_interfaces/visit';
import { EncounterSkeletonComponent } from '../../shared/components/encounter-skeleton/encounter-skeleton.component';
import { LoadingVisibilityController } from '../../shared/utils/loading-visibility';
import { APP_DIALOG_DATA } from '../../shared/components/dialog/dialog.tokens';
import { AppDialogRef } from '../../shared/components/dialog/dialog-ref';

export type SelectVisitFlow = 'start-encounter' | 'encounter' | 'document';

export interface SelectVisitData {
  patient: Subject;
  mode: 'consultation' | 'dictation';
  flow: SelectVisitFlow;
}

/**
 * `back` returns to the still-open patient step, `close` cancels the whole flow, and
 * a payload carries the choice on — `null` meaning the user started without a visit.
 */
export type SelectVisitResult = 'back' | 'close' | { visit: Visit | null };

@Component({
  selector: 'app-select-visit',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatRadioModule,
    MatTableModule,
    EncounterSkeletonComponent,
    TranslateModule,
  ],
  templateUrl: './select-visit.component.html',
})
export class SelectVisitComponent implements OnInit, OnDestroy {
  readonly displayedColumns = [
    'select', 'location', 'tasks', 'consultant', 'referrer',
    'admit', 'discharge', 'visitNumber', 'clinicCode', 'type',
  ];

  visits: FormattedVisit[] = [];
  selectedVisit: FormattedVisit | null = null;
  isLoading = true;
  showLoadingSkeleton = false;

  /** Resolves once the initial visit lookup has settled. Assigned in ngOnInit. */
  loaded!: Promise<void>;

  private readonly loadingVisibility = new LoadingVisibilityController(visible => {
    this.showLoadingSkeleton = visible;
  });

  constructor(
    @Inject(APP_DIALOG_DATA) public readonly data: SelectVisitData,
    private readonly dialogRef: AppDialogRef<SelectVisitComponent, SelectVisitResult>,
    private readonly apiService: ApiService,
  ) {}

  ngOnInit(): void {
    this.loaded = this.loadVisits();
  }

  ngOnDestroy(): void {
    this.loadingVisibility.destroy();
  }

  /** A failed request is indistinguishable from no visits here: either way the user continues without one. */
  async loadVisits(): Promise<void> {
    this.isLoading = true;
    this.loadingVisibility.start();
    try {
      const visits = await this.apiService.getVisits(this.data.patient.id);
      this.visits = visits.map(formatVisit)
    } catch {
      this.visits = [];
    } finally {
      this.selectedVisit = null;
      this.isLoading = false;
      this.loadingVisibility.stop();
    }
  }

  get hasVisits(): boolean {
    return this.visits.length > 0;
  }

  /** The step is shared by every flow, so the copy follows both flow and mode. */
  get primaryActionLabelKey(): string {
    if (this.data.flow === 'start-encounter') {
      return this.data.mode === 'dictation' ? 'Start dictation' : 'Start consultation';
    }
    return 'Save changes';
  }

  get withoutVisitLabelKey(): string {
    return this.data.flow === 'start-encounter'
      ? 'Start without visit'
      : 'Continue without visit';
  }

  get emptyActionLabelKey(): string {
    return this.data.flow === 'start-encounter' ? 'Start' : 'Save';
  }

  get emptyStateHintKey(): string {
    const isDictation = this.data.mode === 'dictation';
    if (this.data.flow === 'start-encounter') {
      return isDictation
        ? 'You can start this dictation without linking a visit'
        : 'You can start this consultation without linking a visit';
    }
    return isDictation
      ? 'You can continue this dictation without linking a visit'
      : 'You can continue this consultation without linking a visit';
  }

  /**
   * The skeleton outlives `isLoading` by the controller's minimum visible time, so the
   * footer follows this instead: no starting the encounter while the list is still masked.
   */
  get isBusy(): boolean {
    return this.isLoading || this.showLoadingSkeleton;
  }

  isSelected(visit: FormattedVisit): boolean {
    return this.selectedVisit?.id === visit.id;
  }

  onVisitClick(visit: FormattedVisit): void {
    this.selectedVisit = this.isSelected(visit) ? null : visit;
  }

  onVisitKeydown(visit: FormattedVisit, event: KeyboardEvent): void {
    if (event.key !== 'Enter') return;
    this.onVisitClick(visit);
  }

  onSelectVisit(visit: FormattedVisit): void {
    this.selectedVisit = visit;
  }

  startWithVisit(): void {
    if (!this.selectedVisit) return;
    this.dialogRef.close({ visit: this.selectedVisit });
  }

  startWithoutVisit(): void {
    this.dialogRef.close({ visit: null });
  }

  goBack(): void {
    this.dialogRef.close('back');
  }

  close(): void {
    this.dialogRef.close('close');
  }
}
